export default function TermsOfServicesPage() {
  return (
    <div className="mx-auto max-w-2xl px-6 pt-40">
      <div className="prose max-w-none">
        <h1 className="mb-8 font-recursive text-3xl font-bold">
          Low Code Plugins Terms of Service Agreement
        </h1>

        <p className="text-lg font-semibold">TERMS OF SERVICE AGREEMENT</p>
        <p className="mb-6 text-sm">Last Updated: December 6, 2024</p>

        <p className="mb-6">
          This Terms of Service Agreement ("Agreement") is entered into between
          Wise Growth Marketing, doing business as Low Code Plugins, ("Low Code
          Plugins") a company incorporated under the laws of the state of Texas,
          and the user ("User") who intends to use the services offered by Low
          Code Plugins. By accessing or using Low Code Plugins' services, User
          acknowledges that they have read, understood, and agree to be bound by
          the terms and conditions of this Agreement. If User does not agree to
          these terms, User may not use the services offered by Low Code
          Plugins.
        </p>

        <ol className="list-decimal space-y-6 pl-6">
          <li>
            <p className="mb-2 font-semibold">Definitions</p>
            <ol className="list-decimal space-y-2 pl-6">
              <li>
                "Services" refers to the software plugins and related web
                applications offered by Low Code Plugins that extend the
                capabilities of various low-code development platforms.
              </li>
              <li>
                "Plugins" refers to any software components, extensions, or
                tools provided by Low Code Plugins for use with low-code
                development platforms.
              </li>
            </ol>
          </li>

          <li>
            <p className="mb-2 font-semibold">Use of Services</p>
            <ol className="list-decimal space-y-2 pl-6">
              <li>
                User agrees to use the Services solely for lawful purposes and
                in compliance with all applicable laws and regulations.
              </li>
              <li>
                User agrees not to reverse engineer, decompile, or attempt to
                derive the source code of the Plugins.
              </li>
              <li>
                User agrees not to use the Services in a manner that would
                violate the intellectual property rights of any third party.
              </li>
              <li>
                User acknowledges and agrees that Low Code Plugins has no
                control over how Users implement the Plugins and does not
                guarantee specific outcomes from their use.
              </li>
            </ol>
          </li>

          <li>
            <p className="mb-2 font-semibold">License and Usage Rights</p>
            <ol className="list-decimal space-y-2 pl-6">
              <li>
                Low Code Plugins grants User a limited, non-exclusive,
                non-transferable license to use the Plugins and Services in
                accordance with this Agreement.
              </li>
              <li>
                User may not sublicense, sell, lease, rent, or otherwise
                transfer the Plugins or Services to any third party.
              </li>
              <li>
                User is solely responsible for ensuring their use of the Plugins
                complies with the terms and conditions of their chosen low-code
                development platform.
              </li>
            </ol>
          </li>

          <li>
            <p className="mb-2 font-semibold">Fees and Payment</p>
            <ol className="list-decimal space-y-2 pl-6">
              <li>
                Low Code Plugins reserves the right to charge fees for the use
                of its Services.
              </li>
              <li>
                User agrees to pay all fees and charges incurred in connection
                with the use of the Services in a timely manner.
              </li>
              <li>
                Low Code Plugins reserves the right to modify its fees and
                payment policies at any time, with notice to User.
              </li>
            </ol>
          </li>

          <li>
            <p className="mb-2 font-semibold">Disclaimer of Warranties</p>
            <ol className="list-decimal space-y-2 pl-6">
              <li>
                Low Code Plugins provides its Services on an "as is" and "as
                available" basis.
              </li>
              <li>
                Low Code Plugins does not warrant that the Services will meet
                User's requirements, be error-free, uninterrupted, or secure.
              </li>
              <li>
                Low Code Plugins disclaims all warranties, whether express or
                implied, including but not limited to, warranties of
                merchantability, fitness for a particular purpose, and
                non-infringement.
              </li>
            </ol>
          </li>

          <li>
            <p className="mb-2 font-semibold">Limitation of Liability</p>
            <ol className="list-decimal space-y-2 pl-6">
              <li>
                Low Code Plugins shall not be liable for any damages arising
                from the use of, inability to use, or reliance on the Services,
                including but not limited to, direct, indirect, incidental,
                special, or consequential damages.
              </li>
              <li>
                Low Code Plugins' liability for any claim arising out of or
                relating to this Agreement shall not exceed the amount paid by
                User to Low Code Plugins for the Services.
              </li>
              <li>
                Low Code Plugins shall not be liable for any damages arising
                from the integration or implementation of the Plugins with
                third-party platforms or services.
              </li>
            </ol>
          </li>

          <li>
            <p className="mb-2 font-semibold">Indemnification</p>
            <ol className="list-decimal space-y-2 pl-6">
              <li>
                User agrees to indemnify, defend, and hold harmless Low Code
                Plugins and its officers, directors, employees, agents, and
                affiliates from and against any and all claims, damages,
                liabilities, costs, and expenses, including reasonable
                attorneys' fees, arising from or related to User's use of the
                Services or User's breach of this Agreement.
              </li>
            </ol>
          </li>

          <li>
            <p className="mb-2 font-semibold">Termination</p>
            <ol className="list-decimal space-y-2 pl-6">
              <li>
                User may terminate this Agreement and their access to the
                Services at any time via the Stripe Customer Portal, accessible
                via the "Billing" section of the Low Code Plugins web
                application
              </li>
              <li>
                Low Code Plugins may terminate this Agreement and User's access
                to the Services at any time, with or without cause, by providing
                notice to User.
              </li>
              <li>
                Upon termination, User's access to the Services will be
                immediately terminated and User must cease all use of the
                Plugins.
              </li>
              <li>
                The provisions of Sections 3, 5, 6, 7, and 9 of this Agreement
                shall survive any termination.
              </li>
            </ol>
          </li>

          <li>
            <p className="mb-2 font-semibold">Governing Law and Jurisdiction</p>
            <ol className="list-decimal space-y-2 pl-6">
              <li>
                This Agreement shall be governed by and construed in accordance
                with the laws of the state of Texas, without giving effect to
                any choice of law or conflict of law provisions.
              </li>
              <li>
                Any dispute arising out of or relating to this Agreement shall
                be resolved exclusively in the state and federal courts located
                in Harris County, Texas.
              </li>
            </ol>
          </li>

          <li>
            <p className="mb-2 font-semibold">Miscellaneous</p>
            <ol className="list-decimal space-y-2 pl-6">
              <li>
                This Agreement constitutes the entire agreement between Low Code
                Plugins and User regarding the Services and supersedes all prior
                agreements and understandings, whether written or oral.
              </li>
              <li>
                Low Code Plugins may assign this Agreement or any of its rights
                or obligations hereunder without User's prior consent.
              </li>
              <li>
                User may not assign this Agreement or any of its rights or
                obligations hereunder without the prior written consent of Low
                Code Plugins.
              </li>
              <li>
                The failure of either party to enforce any provision of this
                Agreement shall not be deemed a waiver of such provision or the
                right to enforce such provision.
              </li>
              <li>
                If any provision of this Agreement is held to be invalid or
                unenforceable, the remaining provisions shall remain in full
                force and effect.
              </li>
            </ol>
          </li>
        </ol>

        <p className="mt-8">
          By using the Services, User agrees to be bound by the terms and
          conditions of this Agreement. If User does not agree to these terms,
          User may not use the Services offered by Low Code Plugins.
        </p>
      </div>
    </div>
  );
}
